import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  IconButton,
  Tooltip,
  Link,
  Avatar,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import ClearIcon from "@mui/icons-material/Clear";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import VerifiedIcon from "@mui/icons-material/Verified";
import { searchContacts } from "../services/api";
import { trackEvent } from "../services/analytics";
import type { Contact } from "../types";

const ROLE_CATEGORIES = [
  "",
  "Commercial Operations",
  "Market Access",
  "Payer Strategy",
  "Launch Team",
  "Device/Delivery",
  "Specialty Pharmacy",
  "Trade & Distribution",
  "Medical Affairs",
  "HEOR",
  "Pharmacovigilance",
  "Digital/Data",
  "Regulatory",
  "CMC/Manufacturing",
  "CDMO/External Manufacturing",
  "Business Development",
  "Supply Chain",
  "Clinical Operations",
  "Quality",
  "R&D",
  "Executive",
];

const SOURCES = [
  "",
  "clinicaltrials_gov",
  "press_release",
  "pubmed",
  "crossref",
  "orcid",
  "snov.io",
  "manual",
];

const SOURCE_LABELS: Record<string, string> = {
  clinicaltrials_gov: "ClinicalTrials.gov",
  press_release: "Press Release",
  pubmed: "PubMed",
  crossref: "CrossRef",
  orcid: "ORCID",
  "snov.io": "Snov.io",
  manual: "Manual Entry",
};

const roleColors: Record<string, string> = {
  "Commercial Operations": "#1565c0",
  "Market Access": "#6a1b9a",
  "Payer Strategy": "#4527a0",
  "Launch Team": "#e65100",
  "Device/Delivery": "#00838f",
  "Specialty Pharmacy": "#2e7d32",
  "Trade & Distribution": "#1a237e",
  "Medical Affairs": "#c62828",
  "HEOR": "#ad1457",
  "Pharmacovigilance": "#f57f17",
  "Digital/Data": "#00897b",
  "CMC/Manufacturing": "#4e342e",
  "Business Development": "#0d47a1",
};

/** Capitalize a name properly: first letter upper, rest lower. */
function capitalizeName(name: string): string {
  if (!name) return name;
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
}

/** Capitalize a full display name like "john smith" -> "John Smith". */
function capitalizeFullName(fullName: string): string {
  if (!fullName) return fullName;
  return fullName
    .split(" ")
    .map(capitalizeName)
    .join(" ");
}

/** Ensure a LinkedIn URL has the https:// prefix. */
function ensureLinkedInUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `https://${url}`;
}

export default function ContactSearch() {
  const [urlParams] = useSearchParams();
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [nameSearch, setNameSearch] = useState("");
  const [companySearch, setCompanySearch] = useState("");
  const [drugSearch, setDrugSearch] = useState(urlParams.get("drug") || "");
  const [roleFilter, setRoleFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await searchContacts({
        name: nameSearch || undefined,
        company: companySearch || undefined,
        drug: drugSearch || undefined,
        role: roleFilter || undefined,
        source: sourceFilter || undefined,
        page: page + 1,
        page_size: rowsPerPage,
      });
      setContacts(data.results);
      setTotal(data.total);
      trackEvent("contact_search", { name: nameSearch, company: companySearch, drug: drugSearch, role: roleFilter, results: data.total });
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [nameSearch, companySearch, drugSearch, roleFilter, sourceFilter, page, rowsPerPage]);

  useEffect(() => {
    const timer = setTimeout(fetchContacts, 300);
    return () => clearTimeout(timer);
  }, [fetchContacts]);

  const hasFilters = nameSearch || companySearch || drugSearch || roleFilter || sourceFilter;

  const clearFilters = () => {
    setNameSearch("");
    setCompanySearch("");
    setDrugSearch("");
    setRoleFilter("");
    setSourceFilter("");
    setPage(0);
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Contact Search
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
            <TextField
              size="small"
              label="Name"
              value={nameSearch}
              onChange={(e) => { setNameSearch(e.target.value); setPage(0); }}
              sx={{ minWidth: 180 }}
            />

            <TextField
              size="small"
              label="Company"
              value={companySearch}
              onChange={(e) => { setCompanySearch(e.target.value); setPage(0); }}
              sx={{ minWidth: 200 }}
            />

            <TextField
              size="small"
              label="Drug Name"
              value={drugSearch}
              onChange={(e) => { setDrugSearch(e.target.value); setPage(0); }}
              sx={{ minWidth: 180 }}
              helperText={drugSearch ? "Finds contacts at drug's sponsor" : ""}
            />

            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Role / Department</InputLabel>
              <Select
                value={roleFilter}
                label="Role / Department"
                onChange={(e) => { setRoleFilter(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All Roles</MenuItem>
                {ROLE_CATEGORIES.filter(Boolean).map((r) => (
                  <MenuItem key={r} value={r}>{r}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Source</InputLabel>
              <Select
                value={sourceFilter}
                label="Source"
                onChange={(e) => { setSourceFilter(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All Sources</MenuItem>
                {SOURCES.filter(Boolean).map((s) => (
                  <MenuItem key={s} value={s}>{SOURCE_LABELS[s] || s}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {hasFilters && (
              <Tooltip title="Clear all filters">
                <IconButton onClick={clearFilters} size="small">
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ ml: "auto" }}>
              {total.toLocaleString()} contacts
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      {/* Results */}
      <Card>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#fafafa" }}>
                    <TableCell>Name</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Company</TableCell>
                    <TableCell>Role Category</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell>Contact</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contacts.map((c: any) => (
                    <TableRow key={c.id} hover>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Avatar sx={{ width: 28, height: 28, bgcolor: "primary.light", fontSize: 12 }}>
                            <PersonIcon sx={{ fontSize: 16 }} />
                          </Avatar>
                          <Box>
                            <Typography fontWeight={600} variant="body2">
                              {capitalizeFullName(c.name)}
                            </Typography>
                            {c.verified && (
                              <VerifiedIcon sx={{ fontSize: 12, color: "success.main", ml: 0.5 }} />
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {c.title || "--"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {c.company || "--"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {c.role_category ? (
                          <Chip
                            label={c.role_category}
                            size="small"
                            sx={{
                              backgroundColor: (roleColors[c.role_category] || "#666") + "18",
                              color: roleColors[c.role_category] || "#666",
                              fontWeight: 600,
                              fontSize: 11,
                            }}
                          />
                        ) : (
                          <Typography variant="caption" color="text.secondary">--</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {c.email ? (
                          <Link href={`mailto:${c.email}`} variant="body2" sx={{ fontSize: 12 }}>
                            {c.email}
                          </Link>
                        ) : (
                          <Typography variant="caption" color="text.secondary">--</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {SOURCE_LABELS[c.source] || c.source || "--"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          {c.email && (
                            <Tooltip title={c.email}>
                              <IconButton size="small" href={`mailto:${c.email}`} component="a">
                                <EmailIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {c.phone && (
                            <Tooltip title={c.phone}>
                              <IconButton size="small" href={`tel:${c.phone}`} component="a">
                                <PhoneIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          {c.linkedin && (
                            <Tooltip title="LinkedIn">
                              <IconButton size="small" href={ensureLinkedInUrl(c.linkedin)!} target="_blank" rel="noopener" component="a">
                                <LinkedInIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {contacts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: "center", py: 4 }}>
                        <Typography color="text.secondary">
                          No contacts found. Run a Contact Discovery sync first, or adjust filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Card>
    </Box>
  );
}
